import { User as CoreUser } from '@policyfly/protobuf'
import { MultipartFormFile, UpdateAccountRequest, AccountType } from '@policyfly/protobuf/patrick'
import { Domain, Role } from '@policyfly/protobuf/pfbingo'
import { ADMIN_ROLES, AUDITOR_ROLES } from '@policyfly/utils/constants'
import { arrayIncludes } from '@policyfly/utils/type'
import { defineStore } from 'pinia'

import { useApiStore } from '@/stores/api'

import { hotReloadStore } from '@/utils/build'
import { hasMatchingId } from '@/utils/compare'

import type { Program, UserAgency, UserDomain, UserProgram } from '@policyfly/protobuf/patrick'

export interface AuthenticationState {
  /**
   * The logged in user's django id.
   */
  legacyId: number | null
  /**
   * The logged in user's bingo id.
   */
  bingoId: string | null
  /**
   * The logged in user's avatar, if available.
   */
  avatar: string | null
  /**
   * The logged in user's first name.
   */
  firstName: string
  /**
   * The logged in user's last name.
   */
  lastName: string
  /**
   * The logged in user's email.
   */
  email: string
  /**
   * The logged in user's account type.
   */
  accountType: AccountType
  /**
   * The currently active program's ids for the Core domain.
   */
  programIds: {
    id?: number
    bingoId?: string
  }
  /**
   * The domains the logged in user belongs to.
   */
  domains: UserDomain[]
}

export const useAuthenticationStore = defineStore('authentication', {
  state: (): AuthenticationState => ({
    legacyId: null,
    bingoId: null,
    avatar: null,
    firstName: '',
    lastName: '',
    email: '',
    accountType: AccountType.ACCOUNT_TYPE_UNSPECIFIED,
    programIds: {},
    domains: [],
  }),

  getters: {
    loaded (): boolean {
      return !!this.firstName
    },
    /**
     * The current Core domain access, if available.
     */
    coreDomain (): UserDomain | null {
      return this.domains.find((d) => d.domain === Domain.DOMAIN_PF_CORE) ?? null
    },
    /**
     * The programs the logged in user has access to in the Core domain.
     */
    programs (): UserProgram[] {
      return this.coreDomain?.programs ?? []
    },
    /**
     * The agencies the logged in user belongs to in the Core domain.
     */
    agencies (): UserAgency[] {
      return this.coreDomain?.agencies ?? []
    },
    currentProgram (): UserProgram | null {
      return this.programs.find((p) => hasMatchingId(p, this.programIds)) ?? null
    },
    /**
     * Turns the current user's details into a Protobuf {@link CoreUser User}.
     */
    coreUser (): CoreUser {
      return CoreUser.create({
        id: this.legacyId ?? undefined,
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        ulid: this.bingoId ?? undefined,
      })
    },
    fullName (): string {
      return `${this.firstName} ${this.lastName}`
    },
    /**
     * Returns all agencies that are relevant to the currently active program.
     */
    currentAgencies (): UserAgency[] {
      if (!this.currentProgram) return []
      const currentProgram = this.currentProgram
      return this.agencies.filter((agency) => {
        return agency.programs.some((program) => {
          return hasMatchingId(currentProgram, program)
        })
      })
    },
    userRole (): Role | null {
      return this.currentProgram?.role || null
    },
    isProgramAdmin (): boolean {
      if (this.isSuperuser) return true
      return arrayIncludes([Role.ROLE_PROGRAM_ADMIN], this.userRole)
    },
    isProgmin (): boolean {
      if (this.isSuperuser) return true
      return arrayIncludes([Role.ROLE_TECHNICIAN, Role.ROLE_UNDERWRITER, Role.ROLE_PROGRAM_ADMIN], this.userRole)
    },
    isInternalRole (): boolean {
      if (this.isSuperuser) return true
      return arrayIncludes([Role.ROLE_TECHNICIAN, Role.ROLE_UNDERWRITER, Role.ROLE_QUALITY_CONTROL, Role.ROLE_PROGRAM_ADMIN], this.userRole)
    },
    isUnderwriterOrProgmin (): boolean {
      if (this.isSuperuser) return true
      return arrayIncludes([Role.ROLE_UNDERWRITER, Role.ROLE_PROGRAM_ADMIN], this.userRole)
    },
    /**
     * Returns true if the current user is listed in {@link AUDITOR_ROLES}.
     * Also referred to as "Read Only" roles.
     */
    isAuditor (): boolean {
      return arrayIncludes(AUDITOR_ROLES, this.userRole)
    },
    /**
     * Returns true if the current user is listed in {@link ADMIN_ROLES}.
     */
    isSuperuser (): boolean {
      return arrayIncludes(ADMIN_ROLES, this.userRole)
    },
    slug (): string {
      return this.currentProgram?.slug ?? ''
    },
  },

  actions: {
    async load (): Promise<void> {
      const apiStore = useApiStore()
      const response = await apiStore.user.account({
        bingoId: this.bingoId ?? undefined,
      })
      this.legacyId = response.userDetail?.legacyId ?? null
      this.avatar = response.userDetail?.avatar ?? ''
      this.firstName = response.userDetail?.firstName ?? ''
      this.lastName = response.userDetail?.lastName ?? ''
      this.email = response.userDetail?.email ?? ''
      this.accountType = response.accountType
      this.domains = response.domains
    },
    async editDetails (
      data: Pick<UpdateAccountRequest, 'firstName' | 'lastName' | 'email'> & { photo: File | null },
    ): Promise<void> {
      const apiStore = useApiStore()
      const request = UpdateAccountRequest.create({
        legacyId: this.legacyId ?? undefined,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        bingoId: this.bingoId ?? undefined,
      })
      if (data.photo) {
        request.avatar = MultipartFormFile.create({
          name: data.photo.name,
          mimeType: data.photo.type,
          content: new Uint8Array(await data.photo.arrayBuffer()),
        })
      }
      const res = await apiStore.user.updateAccount(request)
      if (data.photo && res.avatarUrl) this.avatar = res.avatarUrl
      this.firstName = res.firstName
      this.lastName = res.lastName
      this.email = res.email
    },
    /**
     * Returns true if a default program was assigned, false if no programs exist
     */
    checkDefaultProgram (): boolean {
      if (!this.programs.length) return false
      // switch to the default or first program
      const defaultProgram = this.programs.find((m) => m.defaultProgram) || this.programs[0]!
      this.programIds = {
        id: defaultProgram.id,
        bingoId: defaultProgram.bingoId,
      }
      return true
    },
    /**
     * Returns true if the user has access to the given program id
     */
    hasProgramAccess (program: Pick<Program, 'id' | 'bingoId'>): boolean {
      return this.programs.some((p) => {
        return hasMatchingId(p, program)
      })
    },
    /**
     * Attempts to log the user out of the application and invalidate the session with the API.
     * Removes all tokens and authentication data from stores.
     */
    async logout (): Promise<void> {
      try {
        const apiStore = useApiStore()
        await apiStore.user.logout()
      } catch (err) {
        console.error(`Error invalidating session with API. Clearing local data. ${err}`)
      } finally {
        this.$reset()
      }
    },
  },
})

hotReloadStore(useAuthenticationStore)
